import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDADK from '@angular/common/locales/da';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { ActivatedRoute, DefaultUrlSerializer, Router, RouterModule, UrlSerializer, UrlTree } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';
import { CompetitionRepository } from './+competitions/infrastructure/repositories/competition.repository';
import { CompetitionService } from './+competitions/services/competition.service';
import { SharedModule } from './+shared/shared.module';
import { UpsaleRepository } from './+shop-purchase/infrastructure/repositories/upsale.repository';
import { UpsaleService } from './+shop-purchase/services/upsale.service';
import { AppConfigModule } from './app-config.module';
import { routes } from './app.routing';
import { AccordionComponent } from './components/accordion.component/accordion.component';
import { AppComponent } from './components/app.component/app.component';
import { ContactSupportComponent } from './components/contact-support.component/contact-support/contact-support.component';
import { GapfundingUpsellComponent } from './components/gapfunding-upsell/gapfunding-upsell.component';
import { Hero7DaysCountdownComponent } from './components/heroes/hero-7-days-countdown.component/hero-7-days-countdown.component';
import { CalenderSpotHero } from './components/heroes/hero-calender-spot.component/hero-calender-spot.component';
import { ChristmasHeroSpotComponent } from './components/heroes/hero-christmas-spot.component/christmas-hero-spot.component';
import { CountryMapHeroComponent } from './components/heroes/hero-country-map.component/hero-country-map.component';
import { HeroTvcComponent } from './components/heroes/hero-tvc/hero-tvc.component';
import { HeroWinnerSpotComponent } from './components/heroes/hero-winner-spot.component/hero-winner-spot.component';
import { MillionpakkenHeroComponent } from './components/heroes/millionpakken-hero-spot.component/millionpakken-hero-spot.component';
import { UspHeroComponent } from './components/heroes/usp-hero-spot.component/usp-hero-spot.component';
import { HomeComponent } from './components/home.component/home.component';
import { InfoBankInformationComponent } from './components/info-bank-information.component/info-bank-information.component';
import { InfoCreditCardInfoComponent } from './components/info-credit-card-info.component/info-credit-card-info.component';
import { InfoCustomerAgreementComponent } from './components/info-customer-agreement.component/info-customer-agreement.component';
import { InfoDrawListInfoComponent } from './components/info-draw-list-info.component/info-draw-list-info.component';
import { InfoPricingUpdateComponent } from './components/info-pricing-update.component/info-pricing-update.component';
import { InfoSelectGameComponent } from './components/info-select-game.component/info-select-game.component';
import { InfoComponent } from './components/info.component/info.component';
import { MyWinsDetailsComponent } from './components/my-wins-details.component/my-wins-details.component';
import { NetsPaymentReturnNetaxeptComponent } from './components/nets-payment-return-netaxept.component/nets-payment-return-netaxept.component';
import { OffCanvasComponent } from './components/off-canvas.component/off-canvas.component';
import { OverlayMenuComponent } from './components/overlay-menu.component/overlay-menu.component';
import { PageNotFoundComponent } from './components/page-not-found.component/page-not-found.component';
import { ReceiptStripComponent } from './components/receipt-strip.component/receipt-strip.component';
import { BsReceiptComponent } from './components/receipt.component/bs-receipt.component/bs-receipt.component';
import { ReceiptComponent } from './components/receipt.component/receipt.component';
import { ShoppingStepperComponent } from './components/shopping-stepper.component/shopping-stepper.component';
import { SleekNoteComponent } from './components/sleek-note.component/sleek-note.component';
import { ViewRefDirective } from './directives/view-ref.directive/view-ref.directive';
import { ApiClient } from './infrastructure/api-client/api-client';
import { AuthInterceptor } from './infrastructure/interceptors/auth.interceptor';
import { AuthorizationRepository } from './infrastructure/repositories/authorization.repository';
import { LotteryRepository } from './infrastructure/repositories/lottery.repository';
import { OrderRepository } from './infrastructure/repositories/order.repository';
import { PaymentRepository } from './infrastructure/repositories/payment-repository';
import { PlayerRepository } from './infrastructure/repositories/player.repository';
import { PopupRepository } from './infrastructure/repositories/popup.repository';
import { SubscriptionRepository } from './infrastructure/repositories/subscription.repository';
import { SupportRepository } from './infrastructure/repositories/support.repository';
import { TicketRepository } from './infrastructure/repositories/ticket.repository';
import { TrackingRepository } from './infrastructure/repositories/tracking.repository';
import { SelectivePreloadingStrategy } from './infrastructure/selective-preloading-strategy';
import { LogService } from './services';
import { AppConfigService } from './services/app-config.service';
import { AuthorizationService } from './services/authorization/authorization.service';
import { ClientService } from './services/client.service';
import { ContentService } from './services/content.service';
import { ContextService } from './services/context.service';
import { DawaAddressService } from './services/dawa-address.service';
import { GlobalErrorHandler } from './services/diagnostics/global-error-handler';
import { LocationService } from './services/location.service';
import { LotteryService } from './services/lottery.service';
import { ModalService } from './services/modal-service';
import { NavBarService } from './services/nav-bar.service';
import { NetsPaymentService } from './services/nets-payment-service';
import { OffCanvasService } from './services/off-canvas.service';
import { OrderDisplayDataService } from './services/order-display-data.service';
import { OrderService } from './services/order.service';
import { PaymentService } from './services/payment-service';
import { PopupService } from './services/pop-up.service';
import { PriceService } from './services/price.service';
import { ReceiptService } from './services/receipt-service';
import { AuthorizationGuardService } from './services/route-guards/authorization-guard.service';
import { CheckoutRouteGuardService } from './services/route-guards/checkout-route-guard-service';
import { RoutingService } from './services/routing-service';
import { SplitService } from './services/split.service';
import { StorageService } from './services/storage.service';
import { SubscriptionService } from './services/subscription.service';
import { SupportService } from './services/support.service';
import { TicketService } from './services/ticket.service';
import { TrackingService } from './services/tracking.service';
import { TrackingFacadeService } from './services/tracking/tracking-facade.service';
import { VisitorService } from './services/visitor.service';
import { startup } from './startup';

registerLocaleData(localeDADK);
export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
  parse(url: string): UrlTree {
    // Preserve original casing for login route to maintain OAuth state parameter integrity
    // some how all routes are lowercased and I wonder why?
    if (url.toLowerCase().startsWith('/login')) {
      return super.parse(url);
    }

    // Lowercase all other routes for consistency
    const arrUrl = url.split('?');
    arrUrl[0] = arrUrl[0].toLowerCase();

    return super.parse(arrUrl.join('?'));
  }
}

@NgModule({
  declarations: [
        AppComponent,
        HomeComponent,
        InfoComponent,
        InfoBankInformationComponent,
        InfoCreditCardInfoComponent,
        InfoCustomerAgreementComponent,
        InfoPricingUpdateComponent,
        InfoSelectGameComponent,
        InfoDrawListInfoComponent,
        MyWinsDetailsComponent,
        NetsPaymentReturnNetaxeptComponent,
        OverlayMenuComponent,
        OffCanvasComponent,
        PageNotFoundComponent,
        ShoppingStepperComponent,
        SleekNoteComponent,
        ViewRefDirective,
        ChristmasHeroSpotComponent,
        AccordionComponent,
        MillionpakkenHeroComponent,
        UspHeroComponent,
        HeroTvcComponent,
        HeroWinnerSpotComponent,
        Hero7DaysCountdownComponent,
        CountryMapHeroComponent,
        CalenderSpotHero,
        ContactSupportComponent,
        ReceiptComponent,
        BsReceiptComponent,
        ReceiptStripComponent,
        GapfundingUpsellComponent,
    ],
    imports: [
        RouterModule.forRoot(routes, { 
          preloadingStrategy: SelectivePreloadingStrategy,
          enableTracing: false,
          // Router configurations
          //setupTestingRouter: true, // Enables testing router setup for testing purposes
          //titleStrategy: 'prefix', // Set the strategy for handling title in route navigation
          canceledNavigationResolution: 'replace', // Default resolution for canceled navigations
          paramsInheritanceStrategy: 'emptyOnly', // Inheritance strategy for route parameters
          urlUpdateStrategy: 'deferred', // The URL is updated only after the navigation succeeds
        }),
        AppConfigModule,
        BrowserModule,
        FormsModule,
        SharedModule,
        ReactiveFormsModule
    ],
    providers: [
        ApiClient,
        AuthorizationService,
        AuthorizationRepository,
        AuthorizationGuardService,
        {
            provide: APP_INITIALIZER,
            useFactory: startup,
            deps: [
                ContextService,
                TrackingFacadeService,
                TrackingService,
                Router,
                ActivatedRoute,
                AppConfigService,
            ],
            multi: true,
        },
        AppConfigService,
        CookieService,
        CheckoutRouteGuardService,
        SubscriptionService,
        SubscriptionRepository,
        ContextService,
        DawaAddressService,
        LocationService,
        LotteryService,
        LotteryRepository,
        ModalService,
        NavBarService,
        NetsPaymentService,
        NgbModule,
        OffCanvasService,
        OrderRepository,
        OrderService,
        OrderDisplayDataService,
        ClientService,
        PaymentService,
        PaymentRepository,
        VisitorService,
        PlayerRepository,
        PriceService,
        RoutingService,
        StorageService,
        TicketRepository,
        TicketService,
        CompetitionService,
        CompetitionRepository,
        TrackingFacadeService,
        TrackingRepository,
        TrackingService,
        LogService,
        ReceiptService,
        SupportService,
        UpsaleService,
        UpsaleRepository,
        SupportRepository,
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer,
        },
        { provide: LOCALE_ID, useValue: 'da-dk' },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        SplitService,
        ContentService,
        PopupService,
        PopupRepository,
        provideHttpClient(withInterceptorsFromDi()),
    ],
    bootstrap: [AppComponent]
  })
export class AppModule {}
